/**
 * OutcomeGroupView — one terminal-outcome group inside the expanded Closed area
 * (SE-6862, Figma nodes 80:18294 / 80:18301).
 *
 * A marker dot and a single-phrase header ("SIGNED · 4"), the group's visible
 * cards, and — when the group is capped — the control that reveals the rest.
 *
 * Each group shows ONE card by default. Closed history is context, not working
 * material, so a board that has closed forty players must not push its live
 * stages off screen. Lifting the cap is one-way: there is deliberately no
 * re-collapse control, because re-hiding cards someone just asked to see is a
 * worse outcome than a long panel.
 * @module features/player-kanban/components/organisms/ClosedArea/OutcomeGroup/OutcomeGroupView
 */

import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";
import { memo } from "react";

import { Dot } from "@/components/atoms/Dot";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { KanbanCardOutcome } from "@/generated/graphql";

import ClosedCardRow from "../ClosedCardRow";
import { outcomeDotClass } from "../../../../utils/outcomeDotColor";

import type { ClosedCardEntry, ClosedGroupData } from "../../../../types/types";

/**
 * Cards shown per outcome group before the rest collapse behind "+N more".
 *
 * One, deliberately. The panel's job is to say WHAT was closed and let someone
 * pull on a thread — the full list of everything ever passed on is a report,
 * not board chrome.
 */

/**
 * The one outcome that renders at full strength.
 *
 * Signing is the result the pipeline exists to produce; every other terminal
 * outcome is history that should recede. Same rule the group marker's fill
 * follows — see `outcomeDotColor`.
 */
const UNDIMMED_OUTCOME = KanbanCardOutcome.Signed;

/** Props for {@link OutcomeGroupView}. */
export interface OutcomeGroupViewProps {
  /** The group to render, with its entries already ordered. */
  readonly group: ClosedGroupData;
  /** The entries to draw — the cap is applied by the Container. */
  readonly visibleEntries: readonly ClosedCardEntry[];
  /** How many entries the cap is hiding, zero when none. */
  readonly hiddenCount: number;
  /** Whether the viewer may reopen cards. */
  readonly canReopen: boolean;
  /** Reopen-request handler for a specific closed card. */
  readonly onReopenPress: (entry: ClosedCardEntry) => void;
  /** Lift the cap for this group. */
  readonly onShowMore?: () => void;
}

/**
 * Renders one outcome group.
 * @param props - {@link OutcomeGroupViewProps}
 * @param props.group - The group to render
 * @param props.visibleEntries - The entries to draw
 * @param props.hiddenCount - How many entries the cap is hiding for this group
 * @param props.canReopen - Whether the viewer may reopen
 * @param props.onReopenPress - Reopen-request handler
 * @param props.onShowMore - Lift the cap for this group
 * @returns The outcome group.
 */
const OutcomeGroupView = ({
  group,
  visibleEntries,
  hiddenCount,
  canReopen,
  onReopenPress,
  onShowMore,
}: OutcomeGroupViewProps) => (
  <Stack
    testID={TEST_ID_PLAYER_KANBAN.closedGroupFor(group.outcome)}
    className="w-full gap-1"
  >
    <Stack direction="horizontal" className="items-center gap-1">
      <Dot className={outcomeDotClass(group.outcome)} />
      {/* Kept as ONE text node ("SIGNED · 4") rather than a label and a
            separate count: the group header is a single phrase, and reading it
            as two nodes would let assistive tech announce them apart. */}
      <Text
        variant="micro"
        className="uppercase tracking-wide text-content-secondary"
      >
        {`${group.label.toUpperCase()} · ${group.count}`}
      </Text>
    </Stack>
    {visibleEntries.map(entry => (
      <ClosedCardRow
        key={entry.cardId}
        entry={entry}
        dimmed={group.outcome !== UNDIMMED_OUTCOME}
        canReopen={canReopen}
        onReopenPress={onReopenPress}
      />
    ))}
    {/* Both conditions matter. `hiddenCount` says there is something to
          reveal; `onShowMore` says this surface can actually reveal it. The
          handlers arrive as a partial map keyed by outcome, so a group can be
          truncated with no handler bound — rendering the control then would
          announce an enabled button to assistive tech that does nothing when
          pressed, which is worse than showing no affordance at all. */}
    {hiddenCount > 0 && onShowMore ? (
      <Pressable
        testID={TEST_ID_PLAYER_KANBAN.CLOSED_GROUP_MORE}
        onPress={onShowMore}
        accessibilityRole="button"
        accessibilityLabel={`Show ${hiddenCount} more ${group.label} cards`}
      >
        <Text variant="micro" className="text-content-muted">
          {`+ ${hiddenCount} more`}
        </Text>
      </Pressable>
    ) : null}
  </Stack>
);

OutcomeGroupView.displayName = "OutcomeGroupView";

export default memo(OutcomeGroupView);
